import React, { useCallback, useEffect, useState } from 'react';
import { Modal } from '@/components';
import { ModalProps } from '@mui/base/Modal';
import clsx from 'clsx';
import { I18N_LANGUAGES } from '@/i18n';
import {
  IYachtData,
  IYachtTranslation,
} from '@/types/yacht-management.types';
import { useTranslateHarborContentMutation } from '@/services/HarborManagementService';
import { useSnackbar } from 'notistack';
import { useIntl } from 'react-intl';

export interface YatchPreviewModalProps extends Omit<ModalProps, 'children'> {
  formData: Partial<IYachtData>;
  source_language?: string;
  initialTranslations?: Record<string, IYachtTranslation> | null;
  onTranslationsGenerated?: (
    translations: Record<string, IYachtTranslation>
  ) => void;
}

const hasTranslations = (
  value?: Record<string, IYachtTranslation> | null
): value is Record<string, IYachtTranslation> =>
  Boolean(value && Object.keys(value).length > 0);

const getApiErrorMessage = (error: unknown): string | undefined => {
  if (!error || typeof error !== 'object') {
    return typeof error === 'string' ? error : undefined;
  }

  const maybeError = error as {
    data?: unknown;
    message?: unknown;
    error?: unknown;
  };

  if (maybeError.data) {
    if (typeof maybeError.data === 'string') return maybeError.data;

    if (typeof maybeError.data === 'object') {
      const data = maybeError.data as {
        message?: unknown;
        error?: unknown;
      };

      if (typeof data.message === 'string') return data.message;
      if (typeof data.error === 'string') return data.error;
    }
  }

  if (typeof maybeError.message === 'string') return maybeError.message;
  if (typeof maybeError.error === 'string') return maybeError.error;

  return undefined;
};

interface YachtLanguagePreviewProps {
  language: {
    code: string;
    label: string;
    flag: string;
  };
  name?: string;
  baseDescription?: string;
  translatedDescription?: string;
  isLoading: boolean;
}

const YachtLanguagePreview: React.FC<YachtLanguagePreviewProps> = ({
  language,
  name,
  baseDescription,
  translatedDescription,
  isLoading,
}) => {
  const intl = useIntl();

  const displayDescription =
    translatedDescription || baseDescription || 'Not provided';

  return (
    <div className="bg-gray-100 shadow-sm hover:shadow-md transition-all duration-300 rounded-xl overflow-hidden mb-5 border border-gray-300">
      <div className="bg-gray-200 px-5 py-3 flex items-center justify-between border-b border-gray-100">
        <div className="flex items-center">
          <img
            src={language.flag}
            alt={language.label}
            className="w-6 h-6 mr-3 rounded-sm shadow-sm"
          />
          <h2 className="text-base font-medium text-gray-800 flex items-center">
            {language.label}
            {isLoading && (
              <div className="ml-2 w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
            )}
          </h2>
        </div>
      </div>

      <div className="p-5">
        <div className="mb-4">
          <p className="block text-sm font-medium text-gray-700 mb-2">
            {intl.formatMessage({ id: 'COMMON.NAME' })}
          </p>
          <p className="text-base font-medium text-gray-800 bg-gray-200 px-3 py-2 rounded-lg">
            {name || 'Not provided'}
          </p>
        </div>

        <div className="mb-0">
          <p className="block text-sm font-medium text-gray-700 mb-2">
            {intl.formatMessage({ id: 'COMMON.DESCRIPTION' })}
          </p>
          <p className="text-sm text-gray-700 bg-gray-200 px-3 py-2 rounded-lg min-h-[80px]">
            {displayDescription}
          </p>
        </div>
      </div>
    </div>
  );
};

const YatchPreviewModal: React.FC<YatchPreviewModalProps> = ({
  formData,
  source_language,
  open,
  onClose,
  className,
  initialTranslations,
  onTranslationsGenerated,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const intl = useIntl();
  const [translations, setTranslations] = useState<
    Record<string, IYachtTranslation>
  >(() => initialTranslations ?? formData.translations ?? {});
  const [isLoading, setIsLoading] = useState(false);

  const [translateHarborContent] = useTranslateHarborContentMutation();

  const handleClose = () => {
    onTranslationsGenerated?.(translations);
    onClose?.({}, 'backdropClick');
  };

  useEffect(() => {
    if (!open) return;

    if (hasTranslations(initialTranslations)) {
      setTranslations(initialTranslations);
      return;
    }

    if (hasTranslations(formData.translations)) {
      setTranslations(formData.translations);
      return;
    }

    setTranslations({});
  }, [open, formData.id, formData.translations, initialTranslations]);

  const fetchTranslations = useCallback(async () => {
    if (!formData.description || !formData.description.trim()) {
      enqueueSnackbar('Please enter description to translate', {
        variant: 'warning',
      });
      return;
    }

    setIsLoading(true);
    try {
      const result = await translateHarborContent({
        description: formData.description,
        source_language: source_language || formData.language || 'en',
      }).unwrap();

      const nextTranslations: Record<string, IYachtTranslation> = {};

      if (result && result.translations) {
        Object.entries(result.translations).forEach(([langCode, value]) => {
          nextTranslations[langCode] = {
            description: value?.description,
          };
        });
      }

      setTranslations(nextTranslations);

      if (onTranslationsGenerated) {
        onTranslationsGenerated(nextTranslations);
      }

      const warningMessage = result?.warning_message;
      if (typeof warningMessage === 'string' && warningMessage.trim()) {
        enqueueSnackbar(warningMessage, { variant: 'warning' });
      } else if (Object.keys(nextTranslations).length > 0) {
        enqueueSnackbar(result?.message || 'Translations generated successfully', {
          variant: 'success',
        });
      }
    } catch (error) {
      enqueueSnackbar(
        getApiErrorMessage(error) || 'Failed to generate translations',
        {
          variant: 'error',
        }
      );
    } finally {
      setIsLoading(false);
    }
  }, [
    enqueueSnackbar,
    formData.description,
    source_language,
    translateHarborContent,
    onTranslationsGenerated,
  ]);

  useEffect(() => {
    if (!open) return;

    if (hasTranslations(initialTranslations)) {
      setTranslations(initialTranslations);
      onTranslationsGenerated?.(initialTranslations);
      return;
    }

    if (hasTranslations(formData.translations)) {
      setTranslations(formData.translations);
      onTranslationsGenerated?.(formData.translations);
      return;
    }

    if (hasTranslations(translations)) {
      onTranslationsGenerated?.(translations);
      return;
    }

    void fetchTranslations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <Modal
      open={open}
      onClose={handleClose}
      className={clsx(
        'harbor-preview-root !flex items-center justify-center',
        className
      )}
      aria-labelledby="yatch-preview-modal-title"
    >
      <div className="harbor-preview-modal bg-gray-100 w-full max-w-4xl mx-4 rounded-xl shadow-xl flex flex-col overflow-hidden">
        <div className="modal-header px-6 py-4 bg-gradient-to-r from-blue-600 dark:from-blue-900 to-blue-700 dark:to-blue-800 border-b border-blue-200">
          <div className="modal-header-row flex items-center justify-between">
            <div>
              <h1
                id="yatch-preview-modal-title"
                className="text-xl font-semibold text-white"
              >
                {intl.formatMessage({ id: 'YACHT.PREVIEW.TITLE' })}
              </h1>
              <p className="text-blue-100 text-sm mt-1">
                {intl.formatMessage({ id: 'YACHT.PREVIEW.DESCRIPTION' })}
              </p>
            </div>
          </div>
        </div>

        <div className="p-6">
          <div className="bg-gray-200 rounded-lg p-4 mb-6">
            <div className="info-header-row flex items-start justify-between">
              <div className="flex-1">
                <h2 className="font-semibold text-gray-800 mb-1">
                  {formData.name ||
                    intl.formatMessage({ id: 'COMMON.NOT_SPECIFIED' })}
                </h2>
                <p className="text-sm text-gray-600">
                  {intl.formatMessage({ id: 'COMMON.LOCATION' })}:{' '}
                  {formData.location ||
                    intl.formatMessage({ id: 'COMMON.NOT_SPECIFIED' })}
                </p>
              </div>
            </div>
          </div>

          <div
            tabIndex={0}
            className="lang-list overflow-y-auto max-h-[50vh] pr-2 custom-scrollbar"
          >
            {I18N_LANGUAGES.map((lang) => {
              const isSource = lang.code === (source_language || formData.language || 'en');

              return (
                <YachtLanguagePreview
                  key={lang.code}
                  language={lang}
                  name={formData.name}
                  baseDescription={formData.description}
                  translatedDescription={
                    isSource
                      ? undefined
                      : translations[lang.code]?.description || undefined
                  }
                  isLoading={isLoading}
                />
              );
            })}
          </div>
        </div>

        <div className="px-6 py-4 bg-gray-200 border-t border-gray-100">
          <div className="footer-actions-row flex justify-between items-center">
            <button
              type="button"
              onClick={fetchTranslations}
              disabled={isLoading}
              className="inline-flex items-center px-4 py-2 bg-green-600 contrast:bg-green-800 text-white text-sm font-medium rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 shadow-sm transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="Regenerate translations"
            >
              {isLoading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  {intl.formatMessage({ id: 'COMMON.GENERATING' })}
                </>
              ) : (
                <>
                  <svg
                    className="w-4 h-4 mr-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                    />
                  </svg>
                  {intl.formatMessage({ id: 'COMMON.REGENERATE' })}
                </>
              )}
            </button>
            <button
              type="button"
              onClick={handleClose}
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 shadow-sm transition-colors duration-200"
              aria-label="Close preview modal"
            >
              {intl.formatMessage({ id: 'COMMON.CLOSE' })}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default YatchPreviewModal;
